/**
 * PlayoffGenerator.ts
 * Generates playoff brackets and handles series simulation
 * 
 * Playoff Structure (8 teams):
 * - Division Series: Top 2 from each division play (4 matchups, best of 7)
 * - Conference Championship: Winners of div series in each conference (2 matchups, best of 7)
 * - League Championship: Winners of conference finals (1 matchup, best of 7)
 */

import { ScheduledGame } from './ScheduleGenerator';

export type PlayoffRound = 'division_series' | 'conference_championship' | 'league_championship';
export type SeriesStatus = 'scheduled' | 'in_progress' | 'completed';

export interface PlayoffSeries {
  id: string;
  round: PlayoffRound;
  conference: 'American' | 'National' | null;  // null for league championship
  seriesName: string;         // e.g., "American League Division Series"
  homeTeamId: string;
  awayTeamId: string;
  homeWins: number;
  awayWins: number;
  games: PlayoffGame[];
  winner: string | null;
  status: SeriesStatus;
  startDay: number;
}

export interface PlayoffGame {
  id: string;
  seriesId: string;
  gameNumber: number;         // 1-7
  homeTeamId: string;
  awayTeamId: string;
  homeScore: number | null;
  awayScore: number | null;
  winner: string | null;
  completed: boolean;
}

export interface PlayoffBracket {
  id: string;
  leagueId: string;
  seasonYear: number;
  status: 'pending' | 'in_progress' | 'completed';
  divisionSeries: PlayoffSeries[];
  conferenceChampionships: PlayoffSeries[];
  leagueChampionship: PlayoffSeries | null;
  champion: string | null;
  startDay: number;
  endDay: number | null;
}

export function generatePlayoffBracket(
  leagueId: string,
  seasonYear: number,
  standings: PlayoffStandings[],
  startDay: number
): PlayoffBracket {
  // standings should have 8 teams: top 2 from each division
  // Order: American-East top 2, American-West top 2, National-East top 2, National-West top 2
  
  const divisionSeries: PlayoffSeries[] = [];
  const conferenceChampionships: PlayoffSeries[] = [];
  
  let gameId = 1;
  
  // === DIVISION SERIES (4 matchups) ===
  // American East (teams 0-1), American West (teams 2-3), National East (teams 4-5), National West (teams 6-7)
  
  const matchups = [
    { conf: 'American', div: 'East', homeIdx: 0, awayIdx: 1 },
    { conf: 'American', div: 'West', homeIdx: 2, awayIdx: 3 },
    { conf: 'National', div: 'East', homeIdx: 4, awayIdx: 5 },
    { conf: 'National', div: 'West', homeIdx: 6, awayIdx: 7 },
  ];
  
  let seriesIdx = 1;
  
  matchups.forEach((m, idx) => {
    const homeTeam = standings[m.homeIdx];
    const awayTeam = standings[m.awayIdx];
    
    if (!homeTeam || !awayTeam) return;
    
    const series: PlayoffSeries = {
      id: `ds-${seriesIdx++}`,
      round: 'division_series',
      conference: m.conf as 'American' | 'National',
      seriesName: `${m.conf} ${m.div} Division Series`,
      homeTeamId: homeTeam.teamId,
      awayTeamId: awayTeam.teamId,
      homeWins: 0,
      awayWins: 0,
      games: [],
      winner: null,
      status: 'scheduled',
      startDay: startDay
    };
    
    // Create 7 potential games (best of 7)
    for (let g = 1; g <= 7; g++) {
      const isHome = g <= 3 ? (g % 2 === 1) : (g <= 5 ? (g % 2 === 0) : true);
      series.games.push({
        id: `pg-${gameId++}`,
        seriesId: series.id,
        gameNumber: g,
        homeTeamId: isHome ? homeTeam.teamId : awayTeam.teamId,
        awayTeamId: isHome ? awayTeam.teamId : homeTeam.teamId,
        homeScore: null,
        awayScore: null,
        winner: null,
        completed: false
      });
    }
    
    divisionSeries.push(series);
  });
  
  // === CONFERENCE CHAMPIONSHIPS (2 matchups) ===
  
  // American: winner of East DS vs winner of West DS
  // National: winner of East DS vs winner of West DS
  
  const americanSeries = divisionSeries.filter(s => s.conference === 'American');
  const nationalSeries = divisionSeries.filter(s => s.conference === 'National');
  
  if (americanSeries.length === 2) {
    const champSeries: PlayoffSeries = {
      id: 'cc-american',
      round: 'conference_championship',
      conference: 'American',
      seriesName: 'American League Championship Series',
      homeTeamId: '',  // Will be set when division series winners are determined
      awayTeamId: '',
      homeWins: 0,
      awayWins: 0,
      games: [],
      winner: null,
      status: 'scheduled',
      startDay: startDay + 20  // Approx 10 days per division series
    };
    
    for (let g = 1; g <= 7; g++) {
      const isHome = g <= 3 ? (g % 2 === 1) : (g <= 5 ? (g % 2 === 0) : true);
      champSeries.games.push({
        id: `pg-${gameId++}`,
        seriesId: champSeries.id,
        gameNumber: g,
        homeTeamId: '',  // Will be set when determined
        awayTeamId: '',
        homeScore: null,
        awayScore: null,
        winner: null,
        completed: false
      });
    }
    
    conferenceChampionships.push(champSeries);
  }
  
  if (nationalSeries.length === 2) {
    const champSeries: PlayoffSeries = {
      id: 'cc-national',
      round: 'conference_championship',
      conference: 'National',
      seriesName: 'National League Championship Series',
      homeTeamId: '',
      awayTeamId: '',
      homeWins: 0,
      awayWins: 0,
      games: [],
      winner: null,
      status: 'scheduled',
      startDay: startDay + 20
    };
    
    for (let g = 1; g <= 7; g++) {
      const isHome = g <= 3 ? (g % 2 === 1) : (g <= 5 ? (g % 2 === 0) : true);
      champSeries.games.push({
        id: `pg-${gameId++}`,
        seriesId: champSeries.id,
        gameNumber: g,
        homeTeamId: '',
        awayTeamId: '',
        homeScore: null,
        awayScore: null,
        winner: null,
        completed: false
      });
    }
    
    conferenceChampionships.push(champSeries);
  }
  
  // === LEAGUE CHAMPIONSHIP (1 matchup) ===
  
  const leagueChampionship: PlayoffSeries = {
    id: 'lc-final',
    round: 'league_championship',
    conference: null,
    seriesName: 'World Series',
    homeTeamId: '',
    awayTeamId: '',
    homeWins: 0,
    awayWins: 0,
    games: [],
    winner: null,
    status: 'scheduled',
    startDay: startDay + 40
  };
  
  for (let g = 1; g <= 7; g++) {
    const isHome = g <= 3 ? (g % 2 === 1) : (g <= 5 ? (g % 2 === 0) : true);
    leagueChampionship.games.push({
      id: `pg-${gameId++}`,
      seriesId: leagueChampionship.id,
      gameNumber: g,
      homeTeamId: '',
      awayTeamId: '',
      homeScore: null,
      awayScore: null,
      winner: null,
      completed: false
    });
  }
  
  const bracket: PlayoffBracket = {
    id: `playoffs-${leagueId}-${seasonYear}`,
    leagueId,
    seasonYear,
    status: 'pending',
    divisionSeries,
    conferenceChampionships,
    leagueChampionship,
    champion: null,
    startDay,
    endDay: null
  };
  
  return bracket;
}

export interface PlayoffStandings {
  teamId: string;
  teamName: string;
  division: string;
  wins: number;
  losses: number;
  winPct: number;
}

// Check if a series is complete and return winner
export function checkSeriesWinner(series: PlayoffSeries): string | null {
  if (series.homeWins >= 4) return series.homeTeamId;
  if (series.awayWins >= 4) return series.awayTeamId;
  return null;
}

// Get next game in series
export function getNextPlayoffGame(series: PlayoffSeries): PlayoffGame | null {
  const nextGame = series.games.find(g => !g.completed);
  return nextGame || null;
}

// Simulate a playoff game result
export function simulatePlayoffGame(
  game: PlayoffGame,
  homeTeamRating: number,
  awayTeamRating: number
): { homeScore: number; awayScore: number; winner: string } {
  // Simple simulation based on team ratings
  const baseScore = 3;
  const homeBonus = (homeTeamRating - awayTeamRating) / 20;
  
  const homeScore = Math.max(0, Math.round(baseScore + homeBonus + (Math.random() * 4 - 1)));
  const awayScore = Math.max(0, Math.round(baseScore - homeBonus + (Math.random() * 4 - 1)));
  
  // Handle ties (rare in baseball but possible)
  let finalHome = homeScore;
  let finalAway = awayScore;
  if (finalHome === finalAway) {
    // Home team wins tiebreaker
    if (Math.random() > 0.5) finalHome++;
    else finalAway++;
  }
  
  const winner = finalHome > finalAway ? game.homeTeamId : game.awayTeamId;
  
  return { homeScore: finalHome, awayScore: finalAway, winner };
}
